import { useEffect, useRef, useState } from 'react'

/**
 * Reveals an element once it enters the viewport. Server-safe: `shown` starts
 * false and the observer is only created after mount.
 */
export const useReveal = <T extends HTMLElement>(threshold = 0.15) => {
    const ref = useRef<T | null>(null)
    const [shown, setShown] = useState(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setShown(true)
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setShown(true)
                        observer.disconnect()
                    }
                }
            },
            { threshold, rootMargin: '0px 0px -8% 0px' }
        )

        observer.observe(el)

        return () => {
            observer.disconnect()
        }
    }, [threshold])

    return { ref, shown }
}
